import { useContext, useMemo } from 'react';
import { isDesktop, isMobile, isTablet } from 'react-device-detect';
import AsideBusiness from './Business';
import ineLogo from 'assets/images/logo.png';
import AccountDefaultLogo from 'assets/images/account-avatar.svg';
import {
  Container,
  CustomTrainingAvatar,
  Logo,
  LogoImage,
  NavMenu,
  NavMenuExternalLink,
  NavMenuItem,
  NavMenuLink,
} from './styles';
import './aside.scss';
import { SessionContext } from 'contexts/session';
import Tooltip from 'components/tooltip';
import {
  isDataManager,
  isTeamManagerOrAccountOwner,
  validateRole,
} from 'contexts/session/utilities';
import useAccountPlanData from 'hooks/useAccountPlanData';
import { MENU_ITEMS } from 'constants/menu';

const GetNavItems = ({ items }): JSX.Element => {
  const { account } = useContext(SessionContext);
  const { isExpired, isPastDue } = useAccountPlanData({ account });

  return (
    <>
      {items.map(({ Icon, text, to, external }, index: number) => {
        if (text === 'Account') return null;

        const isCustomTrainingItem = text === MENU_ITEMS.CUSTOM_CONTENT.text;
        const itemClassName = isCustomTrainingItem ? 'CustomTraining' : text;

        const renderItemContent = () => {
          if (isCustomTrainingItem) {
            return (
              <>
                <CustomTrainingAvatar
                  src={account?.logo_url || AccountDefaultLogo}
                  alt={account?.name || 'Account'}
                />
                <span className="custom-training-label">{text}</span>
              </>
            );
          }

          return (
            <>
              {Icon ? <Icon /> : null}
              <span>{text}</span>
            </>
          );
        };

        return (
          <Tooltip
            placement="right"
            title={isExpired ? 'Your account is expired' : text + ' page'}
            key={index}
          >
            {external ? (
              <NavMenuExternalLink
                className={itemClassName}
                disabled={text !== 'Dashboard' && (isExpired || isPastDue)}
                cursor={isExpired ? 'not-allowed' : 'pointer'}
                href={to}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (text !== 'Dashboard' && isExpired) e.preventDefault();
                }}
              >
                {renderItemContent()}
              </NavMenuExternalLink>
            ) : (
              <NavMenuLink
                className={itemClassName}
                disabled={text !== 'Dashboard' && (isExpired || isPastDue)}
                cursor={isExpired ? 'not-allowed' : 'pointer'}
                to={to}
                activeClassName="selected"
                exact
                onClick={(e) => {
                  if (text !== 'Dashboard' && isExpired) e.preventDefault();
                }}
              >
                {renderItemContent()}
              </NavMenuLink>
            )}
          </Tooltip>
        );
      })}
    </>
  );
};

const Aside = ({
  translateY,
  hasCustomContent = false,
}: {
  translateY: string;
  hasCustomContent?: boolean;
}): JSX.Element => {
  const isRaxEnvEnabled = process.env.REACT_APP_RAX_RENTALS_ENABLED === 'True';
  const {
    account,
    user,
    showDrawer,
    isRaxEnabled: isAccountRaxEnabled,
  } = useContext(SessionContext);

  const asideMenuItems = useMemo(() => {
    const menuItems = isRaxEnvEnabled
      ? Object.values(MENU_ITEMS)
      : Object.values(MENU_ITEMS).filter(({ text }) => text !== MENU_ITEMS.RAX.text);

    if (hasCustomContent) {
      return menuItems;
    }

    return menuItems.filter(({ text }) => text !== MENU_ITEMS.CUSTOM_CONTENT.text);
  }, [hasCustomContent, isRaxEnvEnabled]);

  const isRaxEnabled = useMemo(
    () => isAccountRaxEnabled && isRaxEnvEnabled,
    [isAccountRaxEnabled, isRaxEnvEnabled]
  );

  const userRoleIds = useMemo(
    () => user?.uaa_data?.roles?.data?.map(({ id }) => id) || [],
    [user?.uaa_data?.roles?.data]
  );

  const showAccountsManagement =
    user &&
    validateRole({
      roles: userRoleIds,
      email: user?.email,
    });

  const showGenIa = useMemo(() => isDataManager(userRoleIds), [userRoleIds]);

  const asideUpdatedItems = useMemo(() => {
    const { ACCOUNTS, CERTIFICATIONS, USERS, RAX, GENAI, CUSTOM_CONTENT } = MENU_ITEMS;
    const showCertifications = isTeamManagerOrAccountOwner(userRoleIds);
    const adminItems = [ACCOUNTS.text, USERS.text, RAX.text, CUSTOM_CONTENT.text];

    if (showGenIa) adminItems.push(GENAI.text);

    const hiddenItems = [ACCOUNTS.text];

    if (!isRaxEnabled) hiddenItems.push(RAX.text);
    if (!showGenIa) hiddenItems.push(GENAI.text);
    if (!showCertifications) hiddenItems.push(CERTIFICATIONS.text);
    if (!showAccountsManagement)
      return asideMenuItems.filter(({ text }) => !hiddenItems.includes(text));

    return asideMenuItems
      .filter(({ text }) => adminItems.includes(text))
      .sort((a, b) => adminItems.indexOf(a.text) - adminItems.indexOf(b.text));
  }, [asideMenuItems, isRaxEnabled, showAccountsManagement, showGenIa, userRoleIds]);

  return (
    <Container translateY={!showDrawer && translateY}>
      {(!isMobile || isTablet) && (
        <Logo>
          <LogoImage src={ineLogo} alt="INE" />
        </Logo>
      )}

      <NavMenu $hasCustomContent={hasCustomContent}>
        {user && <GetNavItems items={asideUpdatedItems} />}
      </NavMenu>

      {isDesktop && (
        <NavMenuItem>
          <AsideBusiness account={account} />
        </NavMenuItem>
      )}
    </Container>
  );
};

export default Aside;
